import React, { useState, useEffect, useCallback } from 'react';
import { Form, Input, Select, Space, Popconfirm, message, Modal, DatePicker, InputNumber, Switch, Progress, Button, Dropdown, Image, Skeleton, Spin, Tooltip } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendarCheck, faCalendarDay, faExclamationTriangle,
  faClock, faUserPlus, faStar
} from '@fortawesome/free-solid-svg-icons';
import {
  SearchOutlined, PlusOutlined, EditOutlined, DeleteOutlined,
  LockOutlined, UnlockOutlined, CheckCircleOutlined, CloseCircleOutlined,
  TeamOutlined, HistoryOutlined, MoreOutlined, EyeOutlined,
  FilterOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { usePageHeader } from '../../../layouts/AdminLayout';
import OnDemandEventService from '../../../services/OnDemandEventService';
import OnDemandStorageUnitService from '../../../services/OnDemandStorageUnitService';
import OnDemandHCWService from '../../../services/OnDemandHCWService';
import OnDemandPhotoService from '../../../services/OnDemandPhotoService';
import CreateBookingModal from '../../../components/CreateBookingModal';
import {
  PageContainer,
  TopBar,
  FilterBar,
  SearchInput,
  ActionButton,
  FilterButton,
  StyledTable,
  StatusTag,
  StaffingBar,
  StyledModal,
  FormSection,
  EmptyState,
  StatsRow,
  StatCard,
  AssignmentList,
  StatusHistoryList,
  CompactButton,
} from './styled';
import FilterDrawer from './FilterDrawer';

// Workflow step labels for the per-assignment timing audit.
const WORKFLOW_STEP_LABELS = {
  claimed: 'Claimed',
  confirmed: 'Confirmed',
  en_route_storage: 'En route to storage',
  at_storage: 'Arrived at storage',
  kit_picked_up: 'Kit picked up',
  en_route_event: 'En route to event',
  checked_in: 'Checked in at event',
  checked_out: 'Checked out of event',
  event_complete: 'Event complete',
  en_route_post_office: 'En route to post office',
  at_post_office: 'Arrived at post office',
  dropped_off: 'Dropped off at post office',
  no_show: 'No-show',
  canceled: 'Canceled',
};

function formatDurationMs(ms) {
  if (!ms || ms < 0) return '—';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rs = s % 60;
  if (m < 60) return rs ? `${m}m ${rs}s` : `${m}m`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return rm ? `${h}h ${rm}m` : `${h}h`;
}

function formatUSD(n) {
  const v = Number(n) || 0;
  return `$${v.toFixed(2)}`;
}

const { TextArea } = Input;
const { RangePicker } = DatePicker;

const EVENT_STATUSES = {
  open: { label: 'Open', color: '#52c41a', bg: '#f6ffed' },
  locked: { label: 'Locked', color: '#faad14', bg: '#fffbe6' },
  confirmed: { label: 'Confirmed', color: '#1890ff', bg: '#e6f7ff' },
  completed: { label: 'Completed', color: '#722ed1', bg: '#f9f0ff' },
  canceled: { label: 'Canceled', color: '#ff4d4f', bg: '#fff2f0' },
  rescheduled: { label: 'Rescheduled', color: '#888', bg: '#f5f5f5' },
};

const EVENT_TYPES = {
  client_event: 'Client Event',
  shipping_intake: 'Shipping Intake',
  shipping_return: 'Shipping Return',
};

const STAFFING_STATUSES = {
  fully_staffed: { label: 'Fully Staffed', color: '#52c41a' },
  partially_staffed: { label: 'Partially Staffed', color: '#faad14' },
  unstaffed: { label: 'Unstaffed', color: '#ff4d4f' },
};

// Workflow auto-unlocks this many hours before the event start unless an admin
// sets an explicit `workflowUnlockAt`. Mirrors the backend + mobile app logic.
const WORKFLOW_UNLOCK_LEAD_HOURS = 2;
const getWorkflowUnlock = (event) => {
  if (!event) return null;
  if (event.workflowUnlockAt) return dayjs(event.workflowUnlockAt);
  if (event.startDateTime) {
    return dayjs(event.startDateTime).subtract(WORKFLOW_UNLOCK_LEAD_HOURS, 'hour');
  }
  return null;
};

const EventsPage = () => {
  const { setPageHeader } = usePageHeader();

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState(undefined);
  const [staffingFilter, setStaffingFilter] = useState(undefined);

  // Filter drawer
  const [filterDrawerVisible, setFilterDrawerVisible] = useState(false);

  // Modal state
  const [modalVisible, setModalVisible] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  // Booking-based create modal (creates a Booking + linked mobile event)
  const [bookingModalVisible, setBookingModalVisible] = useState(false);

  // Assignment modal
  const [assignModalVisible, setAssignModalVisible] = useState(false);
  const [assigningEvent, setAssigningEvent] = useState(null);
  const [assigningHCW, setAssigningHCW] = useState(null);

  // Status transition modal
  const [transitionModalVisible, setTransitionModalVisible] = useState(false);
  const [transitionEvent, setTransitionEvent] = useState(null);
  const [transitionTarget, setTransitionTarget] = useState(null);
  const [transitionReason, setTransitionReason] = useState('');

  // Detail modal (assignments + history)
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [detailEvent, setDetailEvent] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailRefreshing, setDetailRefreshing] = useState(false);
  const [detailEncounters, setDetailEncounters] = useState([]);
  const [encountersLoading, setEncountersLoading] = useState(false);
  const [photoLoadingKey, setPhotoLoadingKey] = useState(null);
  // Inline photo viewer (modal). { open, url, title, subtitle, side? }
  const [photoViewer, setPhotoViewer] = useState({ open: false, url: null, title: '', subtitle: '' });

  // Reference data
  const [storageUnits, setStorageUnits] = useState([]);
  const [hcws, setHcws] = useState([]);

  // Page header
  useEffect(() => {
    setPageHeader({
      title: 'Events',
      count: events.length,
      loading: loading,
      loadingType: 'spinner',
      breadcrumbs: [
        { label: 'On-Demand', path: '/admin/ondemand/events' },
        { label: 'Events' }
      ]
    });
  }, [setPageHeader, events.length, loading]);

  // Fetch events
  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const response = await OnDemandEventService.getEvents({
        search: searchText,
        status: statusFilter,
        staffingStatus: staffingFilter
      });
      if (response.success) {
        setEvents(response.data);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
      message.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  }, [searchText, statusFilter, staffingFilter]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Fetch reference data
  useEffect(() => {
    const fetchRefs = async () => {
      try {
        const [storageRes, hcwsRes] = await Promise.all([
          OnDemandStorageUnitService.getStorageUnits({ isActive: true }),
          OnDemandHCWService.getHCWs({ approvalStatus: 'approved' })
        ]);
        if (storageRes.success) setStorageUnits(storageRes.data);
        if (hcwsRes.success) setHcws(hcwsRes.data);
      } catch (error) {
        console.error('Error fetching reference data:', error);
      }
    };
    fetchRefs();
  }, []);

  // Stats
  const stats = {
    total: events.length,
    upcoming: events.filter(e => ['open', 'locked', 'confirmed'].includes(e.status) && new Date(e.startDateTime) > new Date()).length,
    needsStaffing: events.filter(e => {
      if (e.status !== 'open') return false;
      const filled = (e.assignedHCWs || []).filter(a => a.status !== 'canceled' && a.status !== 'no_show').length;
      return filled < (e.requiredStaff || 1);
    }).length,
    today: events.filter(e => {
      const start = new Date(e.startDateTime);
      const now = new Date();
      return start.toDateString() === now.toDateString() && !['canceled', 'rescheduled'].includes(e.status);
    }).length,
  };

  // Open create/edit modal
  const openModal = (event = null) => {
    setEditingEvent(event);
    if (event) {
      form.setFieldsValue({
        name: event.name,
        type: event.type,
        dateRange: [dayjs(event.startDateTime), dayjs(event.endDateTime)],
        workflowUnlockAt: event.workflowUnlockAt ? dayjs(event.workflowUnlockAt) : null,
        timezone: event.timezone,
        locationName: event.location?.name,
        street: event.location?.address?.street,
        city: event.location?.address?.city,
        state: event.location?.address?.state,
        zipCode: event.location?.address?.zipCode,
        geofenceRadius: event.location?.geofenceRadius || 200,
        primaryStorageUnit: event.primaryStorageUnit?._id || event.primaryStorageUnit,
        backupStorageUnit: event.backupStorageUnit?._id || event.backupStorageUnit,
        estimatedAttendees: event.estimatedAttendees,
        staffingOverride: event.staffingOverride?.enabled || false,
        requiredStaff: event.requiredStaff,
        waitlistCapacity: event.waitlistCapacity || 0,
        overrideReason: event.staffingOverride?.reason,
        payRate: event.payRate,
        payType: event.payType,
        estimatedHours: event.estimatedHours,
        orgName: event.clientInfo?.organizationName,
        contactName: event.clientInfo?.contactName,
        contactEmail: event.clientInfo?.contactEmail,
        contactPhone: event.clientInfo?.contactPhone,
        notes: event.notes,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        type: 'client_event',
        timezone: 'America/Chicago',
        geofenceRadius: 200,
        payType: 'hourly',
      });
    }
    setModalVisible(true);
  };

  const closeModal = () => {
    setModalVisible(false);
    setEditingEvent(null);
    form.resetFields();
  };

  // Save event
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const eventData = {
        name: values.name,
        type: values.type,
        startDateTime: values.dateRange[0].toISOString(),
        endDateTime: values.dateRange[1].toISOString(),
        workflowUnlockAt: values.workflowUnlockAt ? values.workflowUnlockAt.toISOString() : null,
        timezone: values.timezone,
        location: {
          name: values.locationName,
          address: {
            street: values.street,
            city: values.city,
            state: values.state,
            zipCode: values.zipCode,
          },
          geofenceRadius: values.geofenceRadius || 200,
        },
        primaryStorageUnit: values.primaryStorageUnit || null,
        backupStorageUnit: values.backupStorageUnit || null,
        estimatedAttendees: values.estimatedAttendees || 0,
        waitlistCapacity: values.waitlistCapacity || 0,
        payRate: values.payRate || 0,
        payType: values.payType,
        estimatedHours: values.estimatedHours || 0,
        clientInfo: {
          organizationName: values.orgName,
          contactName: values.contactName,
          contactEmail: values.contactEmail,
          contactPhone: values.contactPhone,
        },
        notes: values.notes,
      };

      if (values.staffingOverride) {
        eventData.requiredStaff = values.requiredStaff;
        eventData.staffingOverride = {
          enabled: true,
          reason: values.overrideReason || '',
        };
      }

      let response;
      if (editingEvent) {
        response = await OnDemandEventService.updateEvent(editingEvent._id, eventData);
      } else {
        response = await OnDemandEventService.createEvent(eventData);
      }

      if (response.success) {
        message.success(response.message);
        closeModal();
        fetchEvents();
      } else {
        message.error(response.message || 'Failed to save event');
      }
    } catch (error) {
      if (error.errorFields) return; // form validation
      console.error('Error saving event:', error);
      message.error(error.response?.data?.message || 'Failed to save event');
    } finally {
      setSaving(false);
    }
  };

  // Delete event
  const handleDelete = async (id) => {
    try {
      const response = await OnDemandEventService.deleteEvent(id);
      if (response.success) {
        message.success('Event deleted');
        fetchEvents();
      } else {
        message.error(response.message);
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to delete event');
    }
  };

  // Open the on-site workflow immediately by setting workflowUnlockAt to now.
  // `reset` instead clears the override so it reverts to auto (2h before start).
  const handleUnlockWorkflow = async (record, { reset = false } = {}) => {
    try {
      const payload = { workflowUnlockAt: reset ? null : new Date().toISOString() };
      const response = await OnDemandEventService.updateEvent(record._id, payload);
      if (response.success) {
        message.success(reset ? 'Workflow unlock reset to automatic' : 'Workflow unlocked for staff');
        fetchEvents();
        if (detailEvent && detailEvent._id === record._id) {
          loadEventDetail(record._id, { silent: true });
        }
      } else {
        message.error(response.message);
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to update workflow unlock');
    }
  };

  // Status transition
  const openTransitionModal = (event, targetStatus) => {
    setTransitionEvent(event);
    setTransitionTarget(targetStatus);
    setTransitionReason('');
    setTransitionModalVisible(true);
  };

  const handleTransition = async () => {
    if (!transitionEvent || !transitionTarget) return;
    if (transitionTarget === 'canceled' && !transitionReason.trim()) {
      message.warning('A reason is required for cancellation');
      return;
    }
    try {
      const response = await OnDemandEventService.transitionStatus(transitionEvent._id, {
        status: transitionTarget,
        reason: transitionReason || undefined,
      });
      if (response.success) {
        message.success(response.message);
        setTransitionModalVisible(false);
        fetchEvents();
        // Also refresh detail if open
        if (detailEvent && detailEvent._id === transitionEvent._id) {
          loadEventDetail(transitionEvent._id);
        }
      } else {
        message.error(response.message);
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to update status');
    }
  };

  // Detail modal
  const loadEventDetail = async (eventId, { silent = false } = {}) => {
    // `silent` keeps the existing content on screen (just overlays a subtle
    // spinner) so add/remove actions don't blank out the whole modal body.
    if (silent) {
      setDetailRefreshing(true);
    } else {
      setDetailLoading(true);
    }
    try {
      const response = await OnDemandEventService.getEvent(eventId);
      if (response.success) {
        setDetailEvent(response.data);
      }
    } catch (error) {
      message.error('Failed to load event details');
    } finally {
      if (silent) {
        setDetailRefreshing(false);
      } else {
        setDetailLoading(false);
      }
    }
  };

  const loadEventEncounters = async (eventId) => {
    setEncountersLoading(true);
    try {
      const response = await OnDemandPhotoService.listEncounters(eventId);
      if (response.success) {
        setDetailEncounters(response.data || []);
      }
    } catch (_error) {
      // Silent — encounters are optional evidence.
      setDetailEncounters([]);
    } finally {
      setEncountersLoading(false);
    }
  };

  const openDetailModal = async (event) => {
    setDetailModalVisible(true);
    setDetailEncounters([]);
    await Promise.all([
      loadEventDetail(event._id),
      loadEventEncounters(event._id),
    ]);
  };

  // Generic evidence-photo opener: resolves a signed/direct URL then opens
  // it inline in a modal viewer. `key` is a unique loading-spinner key so
  // each button can show its own pending state.
  const openEvidencePhoto = async (key, fetcher, { title = 'Evidence photo', subtitle = '' } = {}) => {
    setPhotoLoadingKey(key);
    try {
      const resp = await fetcher();
      if (resp?.success && resp.data?.url) {
        setPhotoViewer({ open: true, url: resp.data.url, title, subtitle });
      } else {
        message.warning(resp?.message || 'Photo not available');
      }
    } catch (err) {
      message.error(err?.response?.data?.message || 'Failed to load photo');
    } finally {
      setPhotoLoadingKey(null);
    }
  };

  // HCW assignment
  const openAssignModal = (event) => {
    setAssigningEvent(event);
    setAssigningHCW(null);
    setAssignModalVisible(true);
  };

  const handleAssignHCW = async () => {
    if (!assigningEvent || !assigningHCW) return;
    try {
      const response = await OnDemandEventService.assignHCW(assigningEvent._id, {
        hcwId: assigningHCW,
      });
      if (response.success) {
        message.success(response.message);
        setAssignModalVisible(false);
        fetchEvents();
        if (detailEvent && detailEvent._id === assigningEvent._id) {
          loadEventDetail(assigningEvent._id, { silent: true });
        }
      } else {
        message.error(response.message);
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to assign HCW');
    }
  };

  const handleRemoveHCW = async (eventId, assignmentId) => {
    try {
      const response = await OnDemandEventService.removeHCW(eventId, assignmentId);
      if (response.success) {
        message.success(response.message);
        fetchEvents();
        if (detailEvent && detailEvent._id === eventId) {
          loadEventDetail(eventId, { silent: true });
        }
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to remove HCW');
    }
  };

  // Designate (or clear) the single HCW responsible for storage pickup and
  // post-office drop-off. Pass assignmentId = null to clear the runner.
  const handleSetKitRunner = async (eventId, assignmentId) => {
    try {
      const response = await OnDemandEventService.setKitRunner(eventId, assignmentId);
      if (response.success) {
        message.success(response.message);
        if (detailEvent && detailEvent._id === eventId) {
          loadEventDetail(eventId, { silent: true });
        }
      } else if (response.message) {
        message.error(response.message);
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to update kit runner');
    }
  };

  // Manually promote a standby (waitlisted) HCW to a guaranteed spot.
  const handlePromoteWaitlist = async (eventId, assignmentId) => {
    try {
      const response = await OnDemandEventService.promoteWaitlist(eventId, assignmentId);
      if (response.success) {
        message.success(response.message);
        fetchEvents();
        if (detailEvent && detailEvent._id === eventId) {
          loadEventDetail(eventId, { silent: true });
        }
      } else if (response.message) {
        message.error(response.message);
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to promote standby worker');
    }
  };

  // Helper: get staffing info
  const getStaffingInfo = (event) => {
    const filled = (event.assignedHCWs || []).filter(a => a.status !== 'canceled' && a.status !== 'no_show').length;
    const required = event.requiredStaff || 1;
    let status = 'unstaffed';
    if (filled >= required) status = 'fully_staffed';
    else if (filled > 0) status = 'partially_staffed';
    return { filled, required, status };
  };

  // Table columns
  const columns = [
    {
      title: 'Date',
      dataIndex: 'startDateTime',
      key: 'date',
      width: 140,
      render: (date) => (
        <div>
          <div style={{ fontWeight: 500 }}>{dayjs(date).format('MMM D, YYYY')}</div>
          <div style={{ fontSize: 12, color: '#888' }}>{dayjs(date).format('h:mm A')}</div>
        </div>
      ),
      sorter: (a, b) => new Date(a.startDateTime) - new Date(b.startDateTime),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Event Name',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      render: (name, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{name}</div>
          <div style={{ fontSize: 12, color: '#888' }}>
            {EVENT_TYPES[record.type] || record.type}
          </div>
        </div>
      ),
    },
    {
      title: 'Location',
      key: 'location',
      ellipsis: true,
      render: (_, record) => {
        const loc = record.location;
        if (!loc || (!loc.name && !loc.address?.city)) return '-';
        return (
          <div>
            {loc.name && <div style={{ fontWeight: 500 }}>{loc.name}</div>}
            {loc.address?.city && (
              <div style={{ fontSize: 12, color: '#888' }}>
                {loc.address.city}{loc.address.state ? `, ${loc.address.state}` : ''}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (status, record) => {
        const info = EVENT_STATUSES[status] || {};
        const unlock = getWorkflowUnlock(record);
        const workflowLocked = unlock && dayjs().isBefore(unlock) &&
          ['open', 'locked', 'confirmed'].includes(status);
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
            <StatusTag style={{ color: info.color, background: info.bg }}>
              {info.label || status}
            </StatusTag>
            {workflowLocked && (
              <span
                style={{ fontSize: 11, color: '#d46b08', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                title={`On-site workflow opens ${unlock.format('MMM D, YYYY h:mm A')}`}
              >
                <LockOutlined style={{ fontSize: 11 }} />
                Opens {unlock.format('MMM D, h:mm A')}
              </span>
            )}
          </div>
        );
      },
    },
    {
      title: 'Staffing',
      key: 'staffing',
      width: 160,
      render: (_, record) => {
        const { filled, required, status } = getStaffingInfo(record);
        const percent = required > 0 ? Math.round((filled / required) * 100) : 0;
        const statusInfo = STAFFING_STATUSES[status] || {};
        return (
          <StaffingBar $status={status}>
            <span className="staffing-text">{filled}/{required}</span>
            <Progress
              percent={percent}
              size="small"
              strokeColor={statusInfo.color || '#d9d9d9'}
              showInfo={false}
            />
          </StaffingBar>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      align: 'center',
      fixed: 'right',
      render: (_, record) => {
        const validTransitions = {
          open: ['locked', 'canceled'],
          locked: ['confirmed', 'canceled'],
          confirmed: ['completed', 'canceled'],
        };
        const transitions = validTransitions[record.status] || [];

        const unlock = getWorkflowUnlock(record);
        const workflowLocked = unlock && dayjs().isBefore(unlock) &&
          ['open', 'locked', 'confirmed'].includes(record.status);

        const items = [
          { key: 'details', label: 'View Details', icon: <EyeOutlined /> },
          ...(record.status === 'open' ? [
            { key: 'edit', label: 'Edit Event', icon: <EditOutlined /> },
          ] : []),
          ...(['open', 'locked'].includes(record.status) ? [
            { key: 'assign', label: 'Assign HCW', icon: <FontAwesomeIcon icon={faUserPlus} style={{ fontSize: 13 }} /> },
          ] : []),
          ...(workflowLocked ? [
            { type: 'divider' },
            { key: 'unlockWorkflow', label: 'Unlock Workflow Now', icon: <UnlockOutlined style={{ color: '#52c41a' }} /> },
          ] : []),
          ...(transitions.length > 0 ? [{ type: 'divider' }] : []),
          ...(transitions.includes('locked') ? [
            { key: 'locked', label: 'Lock Event', icon: <LockOutlined /> },
          ] : []),
          ...(transitions.includes('confirmed') ? [
            { key: 'confirmed', label: 'Confirm Event', icon: <CheckCircleOutlined style={{ color: '#1890ff' }} /> },
          ] : []),
          ...(transitions.includes('completed') ? [
            { key: 'completed', label: 'Mark Completed', icon: <CheckCircleOutlined style={{ color: '#52c41a' }} /> },
          ] : []),
          ...(transitions.includes('canceled') ? [
            { key: 'canceled', label: 'Cancel Event', icon: <CloseCircleOutlined />, danger: true },
          ] : []),
          ...(record.status === 'open' ? [
            { type: 'divider' },
            { key: 'delete', label: 'Delete Event', icon: <DeleteOutlined />, danger: true },
          ] : []),
        ];

        const handleMenuClick = ({ key }) => {
          switch (key) {
            case 'details': openDetailModal(record); break;
            case 'edit': openModal(record); break;
            case 'assign': openAssignModal(record); break;
            case 'unlockWorkflow': handleUnlockWorkflow(record); break;
            case 'locked':
            case 'confirmed':
            case 'completed':
            case 'canceled':
              openTransitionModal(record, key); break;
            case 'delete':
              Modal.confirm({
                title: 'Delete this event?',
                content: 'This cannot be undone.',
                okText: 'Delete',
                okType: 'danger',
                cancelText: 'Cancel',
                onOk: () => handleDelete(record._id),
              });
              break;
            default: break;
          }
        };

        return (
          <Dropdown
            menu={{ items, onClick: handleMenuClick }}
            trigger={['click']}
            placement="bottomRight"
          >
            <Button
              type="text"
              icon={<MoreOutlined style={{ fontSize: 18 }} />}
              style={{ height: 32, width: 32, padding: 0 }}
            />
          </Dropdown>
        );
      },
    },
  ];

  const filteredStorageUnits = storageUnits;

  const overrideEnabled = Form.useWatch('staffingOverride', form);

  return (
    <PageContainer>
      <div className="header-animate-in">
        <StatsRow>
          <StatCard $bgColor="#e6f7ff" $iconColor="#1890ff">
            <div className="stat-icon">
              <FontAwesomeIcon icon={faCalendarCheck} />
            </div>
            <div className="stat-info">
              <div className="stat-value">{stats.total}</div>
              <div className="stat-label">Total Events</div>
            </div>
          </StatCard>
          <StatCard $bgColor="#f6ffed" $iconColor="#52c41a">
            <div className="stat-icon">
              <FontAwesomeIcon icon={faCalendarDay} />
            </div>
            <div className="stat-info">
              <div className="stat-value">{stats.upcoming}</div>
              <div className="stat-label">Upcoming</div>
            </div>
          </StatCard>
          <StatCard $bgColor="#fff2f0" $iconColor="#ff4d4f">
            <div className="stat-icon">
              <FontAwesomeIcon icon={faExclamationTriangle} />
            </div>
            <div className="stat-info">
              <div className="stat-value">{stats.needsStaffing}</div>
              <div className="stat-label">Needs Staffing</div>
            </div>
          </StatCard>
          <StatCard $bgColor="#f9f0ff" $iconColor="#722ed1">
            <div className="stat-icon">
              <FontAwesomeIcon icon={faClock} />
            </div>
            <div className="stat-info">
              <div className="stat-value">{stats.today}</div>
              <div className="stat-label">Today</div>
            </div>
          </StatCard>
        </StatsRow>

        <TopBar>
          <FilterBar>
            <SearchInput
              placeholder="Search events..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
            <FilterButton
              icon={<FilterOutlined />}
              onClick={() => setFilterDrawerVisible(true)}
            >
              Filters
              {(statusFilter || staffingFilter) && (
                <span className="filter-count">
                  {[statusFilter, staffingFilter].filter(Boolean).length}
                </span>
              )}
            </FilterButton>
          </FilterBar>
          <ActionButton type="primary" icon={<PlusOutlined />} onClick={() => setBookingModalVisible(true)}>
            Create Event
          </ActionButton>
        </TopBar>
      </div>

      <div className="table-animate-in">
        {events.length === 0 && !loading ? (
          <EmptyState>
            <div className="icon">
              <FontAwesomeIcon icon={faCalendarCheck} />
            </div>
            <div className="title">No Events Yet</div>
            <div className="description">Create your first on-demand event to start managing staffing.</div>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setBookingModalVisible(true)}>
              Create Event
            </Button>
          </EmptyState>
        ) : (
          <StyledTable
            columns={columns}
            dataSource={events}
            rowKey="_id"
            loading={loading}
            scroll={{ x: 900, y: 'calc(100vh - 380px)' }}
            sticky
            pagination={{
              pageSize: 15,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} events`,
            }}
          />
        )}
      </div>

      {/* Create/Edit Modal */}
      <StyledModal
        title={editingEvent ? 'Edit Event' : 'Create Event'}
        open={modalVisible}
        onCancel={closeModal}
        onOk={handleSave}
        confirmLoading={saving}
        okText={editingEvent ? 'Update' : 'Create'}
        width={720}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <FormSection>
            <div className="section-title">Basic Information</div>
            <Form.Item name="name" label="Event Name" rules={[{ required: true, message: 'Event name is required' }]}>
              <Input placeholder="e.g. Community Health Fair" maxLength={200} />
            </Form.Item>
            <Space style={{ width: '100%' }} size={16}>
              <Form.Item name="type" label="Event Type" style={{ flex: 1 }}>
                <Select>
                  {Object.entries(EVENT_TYPES).map(([key, label]) => (
                    <Select.Option key={key} value={key}>{label}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item name="timezone" label="Timezone" style={{ flex: 1 }}>
                <Select showSearch optionFilterProp="children">
                  <Select.Option value="America/New_York">Eastern</Select.Option>
                  <Select.Option value="America/Chicago">Central</Select.Option>
                  <Select.Option value="America/Denver">Mountain</Select.Option>
                  <Select.Option value="America/Los_Angeles">Pacific</Select.Option>
                </Select>
              </Form.Item>
            </Space>
            <Form.Item name="dateRange" label="Date & Time Range" rules={[{ required: true, message: 'Date range is required' }]}>
              <RangePicker showTime format="YYYY-MM-DD HH:mm" style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              name="workflowUnlockAt"
              label="Workflow Unlock Time"
              tooltip="When HCWs can start the on-site workflow (travel to storage, etc). Leave blank to auto-unlock 2 hours before start."
            >
              <DatePicker showTime format="YYYY-MM-DD HH:mm" style={{ width: '100%' }} placeholder="Auto (2h before start)" />
            </Form.Item>
          </FormSection>

          <FormSection>
            <div className="section-title">Location</div>
            <Form.Item name="locationName" label="Venue / Location Name">
              <Input placeholder="e.g. Community Center" />
            </Form.Item>
            <Space style={{ width: '100%' }} size={16}>
              <Form.Item name="street" label="Street" style={{ flex: 2 }}>
                <Input placeholder="123 Main St" />
              </Form.Item>
              <Form.Item name="city" label="City" style={{ flex: 1 }}>
                <Input placeholder="Dallas" />
              </Form.Item>
            </Space>
            <Space style={{ width: '100%' }} size={16}>
              <Form.Item name="state" label="State" style={{ flex: 1 }}>
                <Input placeholder="TX" />
              </Form.Item>
              <Form.Item name="zipCode" label="Zip Code" style={{ flex: 1 }}>
                <Input placeholder="75201" />
              </Form.Item>
              <Form.Item name="geofenceRadius" label="Geofence (m)" style={{ flex: 1 }}>
                <InputNumber min={50} max={1000} style={{ width: '100%' }} />
              </Form.Item>
            </Space>
          </FormSection>

          <FormSection>
            <div className="section-title">Storage</div>
            <Space style={{ width: '100%' }} size={16}>
              <Form.Item name="primaryStorageUnit" label="Primary Storage Unit" style={{ flex: 1 }}>
                <Select placeholder="Select storage unit" allowClear showSearch optionFilterProp="children">
                  {filteredStorageUnits.map(su => (
                    <Select.Option key={su._id} value={su._id}>{su.name} ({su.code})</Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item name="backupStorageUnit" label="Backup Storage Unit" style={{ flex: 1 }}>
                <Select placeholder="Select backup" allowClear showSearch optionFilterProp="children">
                  {filteredStorageUnits.map(su => (
                    <Select.Option key={su._id} value={su._id}>{su.name} ({su.code})</Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Space>
          </FormSection>

          <FormSection>
            <div className="section-title">Staffing</div>
            <Space style={{ width: '100%' }} size={16}>
              <Form.Item name="estimatedAttendees" label="Estimated Attendees" style={{ flex: 1 }}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="staffingOverride" label="Override Staff Count" valuePropName="checked" style={{ flex: 1 }}>
                <Switch />
              </Form.Item>
            </Space>
            {overrideEnabled && (
              <Space style={{ width: '100%' }} size={16}>
                <Form.Item
                  name="requiredStaff"
                  label="Required Staff"
                  style={{ flex: 1 }}
                  rules={overrideEnabled ? [{ required: true, message: 'Required' }] : []}
                >
                  <InputNumber min={1} max={50} style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item name="overrideReason" label="Override Reason" style={{ flex: 2 }}>
                  <Input placeholder="Reason for override" />
                </Form.Item>
              </Space>
            )}
            <Form.Item
              name="waitlistCapacity"
              label="Standby / Overflow Slots"
              tooltip="Extra workers who can still see and claim this event as standby beyond the required staff. They aren't guaranteed a spot and are promoted when a confirmed worker calls out. 0 disables the waitlist."
            >
              <InputNumber min={0} max={50} style={{ width: '100%' }} placeholder="0" />
            </Form.Item>
          </FormSection>

          <FormSection>
            <div className="section-title">Compensation</div>
            <Space style={{ width: '100%' }} size={16}>
              <Form.Item name="payRate" label="Pay Rate ($)" style={{ flex: 1 }}>
                <InputNumber min={0} precision={2} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="payType" label="Pay Type" style={{ flex: 1 }}>
                <Select>
                  <Select.Option value="hourly">Hourly</Select.Option>
                  <Select.Option value="flat">Flat Rate</Select.Option>
                </Select>
              </Form.Item>
              <Form.Item name="estimatedHours" label="Est. Hours" style={{ flex: 1 }}>
                <InputNumber min={0} step={0.5} style={{ width: '100%' }} />
              </Form.Item>
            </Space>
          </FormSection>

          <FormSection>
            <div className="section-title">Client Information</div>
            <Space style={{ width: '100%' }} size={16}>
              <Form.Item name="orgName" label="Organization" style={{ flex: 1 }}>
                <Input placeholder="Organization name" />
              </Form.Item>
              <Form.Item name="contactName" label="Contact Name" style={{ flex: 1 }}>
                <Input placeholder="Contact name" />
              </Form.Item>
            </Space>
            <Space style={{ width: '100%' }} size={16}>
              <Form.Item name="contactEmail" label="Contact Email" style={{ flex: 1 }}>
                <Input placeholder="email@example.com" />
              </Form.Item>
              <Form.Item name="contactPhone" label="Contact Phone" style={{ flex: 1 }}>
                <Input placeholder="(555) 555-5555" />
              </Form.Item>
            </Space>
          </FormSection>

          <Form.Item name="notes" label="Notes">
            <TextArea rows={3} placeholder="Any additional notes..." maxLength={2000} showCount />
          </Form.Item>
        </Form>
      </StyledModal>

      {/* Status Transition Modal */}
      <Modal
        title={`${transitionTarget === 'canceled' ? 'Cancel' : transitionTarget === 'locked' ? 'Lock' : transitionTarget === 'confirmed' ? 'Confirm' : 'Complete'} Event`}
        open={transitionModalVisible}
        onCancel={() => setTransitionModalVisible(false)}
        onOk={handleTransition}
        okText={transitionTarget === 'canceled' ? 'Cancel Event' : 'Confirm'}
        okButtonProps={transitionTarget === 'canceled' ? { danger: true } : {}}
      >
        <p>
          {transitionTarget === 'canceled' && `Are you sure you want to cancel "${transitionEvent?.name}"?`}
          {transitionTarget === 'locked' && `Lock "${transitionEvent?.name}"? No more HCW claims will be allowed.`}
          {transitionTarget === 'confirmed' && `Confirm "${transitionEvent?.name}"? This marks the event as ready for execution.`}
          {transitionTarget === 'completed' && `Mark "${transitionEvent?.name}" as completed?`}
        </p>
        <Input.TextArea
          placeholder={transitionTarget === 'canceled' ? 'Reason for cancellation (required)' : 'Reason (optional)'}
          value={transitionReason}
          onChange={e => setTransitionReason(e.target.value)}
          rows={3}
        />
      </Modal>

      {/* Assign HCW Modal */}
      <Modal
        title="Assign Healthcare Worker"
        open={assignModalVisible}
        onCancel={() => setAssignModalVisible(false)}
        onOk={handleAssignHCW}
        okText="Assign"
        okButtonProps={{ disabled: !assigningHCW, className: 'compact-modal-btn' }}
        cancelButtonProps={{ className: 'compact-modal-btn' }}
      >
        <p>Assign an approved HCW to <strong>{assigningEvent?.name}</strong></p>
        <Select
          placeholder="Select healthcare worker"
          value={assigningHCW}
          onChange={setAssigningHCW}
          style={{ width: '100%' }}
          showSearch
          optionFilterProp="children"
        >
          {hcws
            .filter(h => {
              // Exclude already-assigned HCWs
              const assigned = (assigningEvent?.assignedHCWs || [])
                .filter(a => a.status !== 'canceled')
                .map(a => (a.hcw?._id || a.hcw)?.toString());
              return !assigned.includes(h._id?.toString());
            })
            .map(h => (
              <Select.Option key={h._id} value={h._id}>
                {h.firstName} {h.lastName} — {h.email}
              </Select.Option>
            ))
          }
        </Select>
      </Modal>

      {/* Event Detail Modal (Assignments + History) */}
      <StyledModal
        title={detailEvent?.name || 'Event Details'}
        open={detailModalVisible}
        onCancel={() => { setDetailModalVisible(false); setDetailEvent(null); setDetailEncounters([]); }}
        footer={null}
        width={640}
      >
        {detailLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>Loading...</div>
        ) : detailEvent ? (
          <div style={{ position: 'relative', transition: 'opacity 0.2s ease', opacity: detailRefreshing ? 0.55 : 1 }}>
            {detailRefreshing && (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  paddingTop: 80,
                  zIndex: 5,
                  pointerEvents: 'none',
                }}
              >
                <Spin />
              </div>
            )}
            {/* Basic info summary */}
            <FormSection>
              <div className="section-title">
                <FontAwesomeIcon icon={faCalendarCheck} /> Event Info
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px', fontSize: 14 }}>
                <div><strong>Status:</strong> {EVENT_STATUSES[detailEvent.status]?.label}</div>
                <div><strong>Type:</strong> {EVENT_TYPES[detailEvent.type]}</div>
                <div><strong>Date:</strong> {dayjs(detailEvent.startDateTime).format('MMM D, YYYY h:mm A')}</div>
                <div><strong>Location:</strong> {detailEvent.location?.name || detailEvent.location?.address?.city || '-'}</div>
                <div><strong>Required Staff:</strong> {detailEvent.requiredStaff}</div>
                {(() => {
                  const unlock = getWorkflowUnlock(detailEvent);
                  if (!unlock) return null;
                  const locked = dayjs().isBefore(unlock);
                  return (
                    <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                      <span>
                        <strong>Workflow:</strong>{' '}
                        {locked ? (
                          <span style={{ color: '#d46b08' }}>
                            <LockOutlined style={{ marginRight: 4 }} />
                            Opens {unlock.format('MMM D, YYYY h:mm A')}
                            {detailEvent.workflowUnlockAt ? '' : ' (auto · 2h before start)'}
                          </span>
                        ) : (
                          <span style={{ color: '#52c41a' }}>
                            <CheckCircleOutlined style={{ marginRight: 4 }} />
                            Unlocked for staff
                          </span>
                        )}
                      </span>
                      {locked && ['open', 'locked', 'confirmed'].includes(detailEvent.status) && (
                        <CompactButton
                          $h={24}
                          $px={12}
                          icon={<UnlockOutlined />}
                          onClick={() => handleUnlockWorkflow(detailEvent)}
                        >
                          Unlock now
                        </CompactButton>
                      )}
                    </div>
                  );
                })()}
              </div>
            </FormSection>

            {/* Assigned HCWs */}
            <FormSection>
              <div className="section-title" style={{ justifyContent: 'space-between' }}>
                <span><TeamOutlined /> Assigned Staff ({(detailEvent.assignedHCWs || []).filter(a => !['canceled', 'no_show', 'waitlisted'].includes(a.status)).length}/{detailEvent.requiredStaff})
                  {(detailEvent.assignedHCWs || []).some(a => a.status === 'waitlisted') && (
                    <span style={{ color: '#d46b08', fontWeight: 500, marginLeft: 8 }}>
                      · {(detailEvent.assignedHCWs || []).filter(a => a.status === 'waitlisted').length} standby
                      {detailEvent.waitlistCapacity ? ` / ${detailEvent.waitlistCapacity}` : ''}
                    </span>
                  )}
                </span>
                {['open', 'locked'].includes(detailEvent.status) && (
                  <CompactButton
                    $h={26}
                    $px={14}
                    icon={<FontAwesomeIcon icon={faUserPlus} />}
                    onClick={() => openAssignModal(detailEvent)}
                  >
                    Assign
                  </CompactButton>
                )}
              </div>
              {detailEvent.payoutSummary && (
                <div style={{
                  display: 'flex', flexWrap: 'wrap', gap: 16, padding: '10px 14px',
                  marginBottom: 12, borderRadius: 8, background: '#f6ffed',
                  border: '1px solid #b7eb8f', fontSize: 13
                }}>
                  <span><strong>Labor:</strong> {formatUSD(detailEvent.payoutSummary.totalLabor)}</span>
                  <span>
                    <strong>Mileage:</strong> {formatUSD(detailEvent.payoutSummary.totalMileage)}
                    {' '}({(detailEvent.payoutSummary.totalMiles || 0).toFixed(2)} mi @ {formatUSD(detailEvent.payoutSummary.ratePerMile)}/mi)
                  </span>
                  <span style={{ marginLeft: 'auto', fontWeight: 600 }}>
                    Total payout: {formatUSD(detailEvent.payoutSummary.grandTotal)}
                  </span>
                </div>
              )}
              <AssignmentList>
                {(detailEvent.assignedHCWs || []).length === 0 ? (
                  <div className="no-assignments">No healthcare workers assigned yet</div>
                ) : (
                  detailEvent.assignedHCWs
                    .filter(a => a.status !== 'canceled')
                    .map(assignment => (
                      <div className="assignment-item" key={assignment._id} style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                          <div className="hcw-info">
                            <div>
                              <div className="hcw-name">
                                {assignment.hcw?.firstName} {assignment.hcw?.lastName}
                                {assignment.status === 'waitlisted' && (
                                  <StatusTag style={{ marginLeft: 8, color: '#d46b08', background: '#fff7e6' }}>
                                    Standby
                                  </StatusTag>
                                )}
                                {assignment.isKitRunner && (
                                  <StatusTag style={{ marginLeft: 8, color: '#faad14', background: '#fffbe6' }}>
                                    <FontAwesomeIcon icon={faStar} style={{ marginRight: 4 }} />
                                    Kit Runner
                                  </StatusTag>
                                )}
                              </div>
                              <div className="hcw-contact">
                                {assignment.hcw?.email} • {assignment.status}
                              </div>
                            </div>
                          </div>
                          <div className="assignment-actions">
                            <StatusTag style={{
                              color: assignment.status === 'waitlisted' ? '#d46b08' : assignment.status === 'claimed' ? '#1890ff' : '#52c41a',
                              background: assignment.status === 'waitlisted' ? '#fff7e6' : assignment.status === 'claimed' ? '#e6f7ff' : '#f6ffed',
                            }}>
                              {assignment.status}
                            </StatusTag>
                            {assignment.status === 'waitlisted' ? (
                              <Tooltip title="Promote this standby worker to a guaranteed spot">
                                <CompactButton
                                  $h={22}
                                  $px={10}
                                  $fs={11}
                                  onClick={() => handlePromoteWaitlist(detailEvent._id, assignment._id)}
                                >
                                  Promote
                                </CompactButton>
                              </Tooltip>
                            ) : (
                              <Tooltip title={assignment.isKitRunner
                                ? 'Clear kit runner (everyone runs the full workflow)'
                                : 'Make this HCW the only one who handles storage pickup & post-office drop-off'}>
                                <CompactButton
                                  $h={22}
                                  $px={10}
                                  $fs={11}
                                  onClick={() => handleSetKitRunner(
                                    detailEvent._id,
                                    assignment.isKitRunner ? null : assignment._id
                                  )}
                                >
                                  <FontAwesomeIcon icon={faStar} style={{ marginRight: 4 }} />
                                  {assignment.isKitRunner ? 'Clear runner' : 'Make runner'}
                                </CompactButton>
                              </Tooltip>
                            )}
                            {assignment.kitPhoto?.url && (
                              <Button
                                size="small"
                                type="link"
                                icon={<EyeOutlined />}
                                loading={photoLoadingKey === `kit-${assignment._id}`}
                                onClick={() => openEvidencePhoto(
                                  `kit-${assignment._id}`,
                                  () => OnDemandPhotoService.getKitPhotoUrl(detailEvent._id, assignment._id),
                                  {
                                    title: `Kit photo — ${assignment.hcw?.firstName || ''} ${assignment.hcw?.lastName || ''}`.trim(),
                                    subtitle: assignment.kitPhoto?.uploadedAt
                                      ? `Uploaded ${dayjs(assignment.kitPhoto.uploadedAt).format('MMM D, YYYY h:mm A')}`
                                      : ''
                                  }
                                )}
                                title={assignment.kitPhoto?.uploadedAt ? `Uploaded ${dayjs(assignment.kitPhoto.uploadedAt).format('MMM D, h:mm A')}` : 'Kit photo'}
                              >
                                Kit photo
                              </Button>
                            )}
                            {['open', 'locked'].includes(detailEvent.status) && (
                              <Popconfirm
                                title="Remove assignment?"
                                overlayClassName="compact-popconfirm"
                                okText="Remove"
                                cancelText="Cancel"
                                okButtonProps={{
                                  danger: true,
                                  size: 'small',
                                  style: { borderRadius: 6 },
                                }}
                                cancelButtonProps={{
                                  size: 'small',
                                  style: { borderRadius: 6 },
                                }}
                                onConfirm={() => handleRemoveHCW(detailEvent._id, assignment._id)}
                              >
                                <CompactButton
                                  $h={22}
                                  $px={10}
                                  $fs={11}
                                  $danger
                                >
                                  Remove
                                </CompactButton>
                              </Popconfirm>
                            )}
                          </div>
                        </div>

                        {/* Soft geofence: the app never blocks a worker who is
                            away from the site (e.g. training with the team),
                            but admins can see where they verified from. */}
                        {[
                          ['storage', 'Kit pickup'],
                          ['event', 'Event check-in'],
                        ]
                          .filter(([kind]) => assignment.gpsVerification?.[`${kind}WithinGeofence`] === false)
                          .map(([kind, label]) => {
                            const meters = assignment.gpsVerification?.[`${kind}DistanceMeters`];
                            const away = meters >= 1609
                              ? `${(meters / 1609.344).toFixed(1)} mi`
                              : `${meters} m`;
                            return (
                              <div key={kind} style={{ fontSize: 12, color: '#ad6800', background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 6, padding: '4px 8px' }}>
                                {label} recorded {away} from the {kind === 'event' ? 'event location' : 'storage unit'}
                              </div>
                            );
                          })}

                        {/* Per-assignment workflow timing audit. Renders each
                            recorded transition with the elapsed time from the
                            previous step so admins can verify pacing. */}
                        {Array.isArray(assignment.statusHistory) && assignment.statusHistory.length > 0 && (
                          <details style={{ background: '#fafbfc', border: '1px solid #f0f0f0', borderRadius: 8, padding: '8px 12px' }}>
                            <summary style={{ cursor: 'pointer', fontSize: 12, color: '#595959', fontWeight: 600 }}>
                              Workflow timing ({assignment.statusHistory.length} step{assignment.statusHistory.length === 1 ? '' : 's'})
                              {' · Total: '}
                              {formatDurationMs(
                                assignment.statusHistory.reduce((sum, e) => sum + (e.durationFromPrevMs || 0), 0)
                              )}
                            </summary>
                            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                              {assignment.statusHistory.map((entry, idx) => {
                                const label = WORKFLOW_STEP_LABELS[entry.status] || entry.status;
                                const fromLabel = entry.fromStatus
                                  ? (WORKFLOW_STEP_LABELS[entry.fromStatus] || entry.fromStatus)
                                  : null;
                                const isBackward = entry.fromStatus && entry.fromStatus !== entry.status &&
                                  ['en_route_storage','at_storage','kit_picked_up','en_route_event','checked_in','checked_out','event_complete','en_route_post_office','at_post_office','dropped_off']
                                    .indexOf(entry.status) < ['en_route_storage','at_storage','kit_picked_up','en_route_event','checked_in','checked_out','event_complete','en_route_post_office','at_post_office','dropped_off']
                                    .indexOf(entry.fromStatus);
                                return (
                                  <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, fontSize: 12, color: '#262626' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                                      <span style={{
                                        width: 8, height: 8, borderRadius: '50%',
                                        background: isBackward ? '#ff7875' : '#1292A4',
                                        flexShrink: 0,
                                      }} />
                                      <span style={{ fontWeight: 500 }}>{label}</span>
                                      {fromLabel && (
                                        <span style={{ color: '#8c8c8c' }}>
                                          {' '}from {fromLabel}{isBackward ? ' (back)' : ''}
                                        </span>
                                      )}
                                      {entry.source && entry.source !== 'mobile' && (
                                        <span style={{ color: '#8c8c8c' }}> · {entry.source}</span>
                                      )}
                                    </div>
                                    <div style={{ display: 'flex', gap: 10, color: '#8c8c8c', flexShrink: 0 }}>
                                      <span title="Elapsed since previous step">
                                        +{formatDurationMs(entry.durationFromPrevMs)}
                                      </span>
                                      <span>{dayjs(entry.at).format('MMM D, h:mm:ss A')}</span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </details>
                        )}
                        {assignment.payout && (assignment.payout.total > 0 || assignment.payout.mileage.totalMiles > 0) && (
                          <details style={{ background: '#fafbfc', border: '1px solid #f0f0f0', borderRadius: 8, padding: '8px 12px', marginTop: 6 }}>
                            <summary style={{ cursor: 'pointer', fontSize: 12, color: '#595959', fontWeight: 600 }}>
                              Pay &amp; mileage · {formatUSD(assignment.payout.total)}
                            </summary>
                            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, color: '#262626' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                                <span>
                                  Labor ({assignment.payout.labor.payType === 'flat'
                                    ? 'flat'
                                    : `${assignment.payout.labor.workedHours}h @ ${formatUSD(assignment.payout.labor.payRate)}/hr`})
                                  {assignment.payout.labor.hoursBasis === 'estimated' && (
                                    <span style={{ color: '#fa8c16' }}> · est.</span>
                                  )}
                                </span>
                                <span style={{ fontWeight: 500 }}>{formatUSD(assignment.payout.labor.payout)}</span>
                              </div>
                              {['storageToEvent', 'eventToPostOffice'].map((legKey) => {
                                const leg = assignment.payout.mileage.legs[legKey];
                                const legLabel = legKey === 'storageToEvent'
                                  ? 'Kit pickup → Event'
                                  : 'Event → Drop-off';
                                return (
                                  <div key={legKey} style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                                    <span style={{ color: leg.paid ? '#262626' : '#8c8c8c' }}>
                                      {legLabel} · {leg.miles.toFixed(2)} mi
                                      {!leg.paid && ' (unpaid)'}
                                    </span>
                                    <span style={{ fontWeight: 500 }}>{formatUSD(leg.payout)}</span>
                                  </div>
                                );
                              })}
                              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, borderTop: '1px solid #f0f0f0', paddingTop: 6, fontWeight: 600 }}>
                                <span>Total · {assignment.payout.mileage.totalMiles.toFixed(2)} mi</span>
                                <span>{formatUSD(assignment.payout.total)}</span>
                              </div>
                            </div>
                          </details>
                        )}
                      </div>
                    ))
                )}
              </AssignmentList>
            </FormSection>

            {/* Post-office drop-off evidence */}
            {detailEvent.postOffice && (detailEvent.postOffice.dropoffAt || detailEvent.postOffice.name) && (
              <FormSection>
                <div className="section-title">
                  <FontAwesomeIcon icon={faCalendarDay} /> Post-Office Drop-Off
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 14 }}>
                  <div><strong>Location:</strong> {detailEvent.postOffice.name || '—'}</div>
                  <div>
                    <strong>Dropped off:</strong>{' '}
                    {detailEvent.postOffice.dropoffAt
                      ? dayjs(detailEvent.postOffice.dropoffAt).format('MMM D, YYYY h:mm A')
                      : '—'}
                  </div>
                  {detailEvent.postOffice.address && (
                    <div style={{ gridColumn: '1 / -1' }}>
                      <strong>Address:</strong> {detailEvent.postOffice.address}
                    </div>
                  )}
                </div>
                {detailEvent.postOffice.dropoffPhotoUrl && (
                  <div style={{ marginTop: 10 }}>
                    <Button
                      size="small"
                      icon={<EyeOutlined />}
                      loading={photoLoadingKey === 'dropoff'}
                      onClick={() => openEvidencePhoto(
                        'dropoff',
                        () => OnDemandPhotoService.getDropoffPhotoUrl(detailEvent._id),
                        {
                          title: 'Drop-off proof photo',
                          subtitle: detailEvent.postOffice?.name || ''
                        }
                      )}
                    >
                      View drop-off proof photo
                    </Button>
                  </div>
                )}
              </FormSection>
            )}

            {/* Patient encounters captured during the event (blood-card photos
                — PHI; admins with ondemand_management can view via signed URL). */}
            {(encountersLoading || detailEncounters.length > 0) && (
              <FormSection>
                <div className="section-title">
                  <TeamOutlined /> Patient Encounters ({encountersLoading ? '…' : detailEncounters.length})
                </div>
                {encountersLoading ? (
                  <Skeleton active paragraph={{ rows: 2 }} />
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {detailEncounters.map((enc) => (
                      <div
                        key={enc._id}
                        style={{
                          background: '#fafbfc',
                          border: '1px solid #f0f0f0',
                          borderRadius: 8,
                          padding: '10px 12px',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 6,
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
                          <div style={{ fontSize: 14, fontWeight: 600 }}>
                            {enc.firstName} {enc.lastName}
                            {enc.dob && (
                              <span style={{ fontWeight: 400, color: '#8c8c8c', marginLeft: 8 }}>
                                DOB {dayjs(enc.dob).format('MMM D, YYYY')}
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                            {enc.capturedAt ? dayjs(enc.capturedAt).format('MMM D, h:mm A') : ''}
                            {enc.hcw && (
                              <> · by {enc.hcw.firstName} {enc.hcw.lastName}</>
                            )}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                          <StatusTag style={{ background: '#f6ffed', color: '#52c41a' }}>{enc.status || 'captured'}</StatusTag>
                          <Button
                            size="small"
                            icon={<EyeOutlined />}
                            loading={photoLoadingKey === `enc-${enc._id}-front`}
                            onClick={() => openEvidencePhoto(
                              `enc-${enc._id}-front`,
                              () => OnDemandPhotoService.getEncounterCardPhotoUrl(enc._id, 'front'),
                              {
                                title: `Blood card (front) — ${enc.firstName} ${enc.lastName}`,
                                subtitle: enc.capturedAt ? `Captured ${dayjs(enc.capturedAt).format('MMM D, YYYY h:mm A')}` : ''
                              }
                            )}
                          >
                            Front
                          </Button>
                          <Button
                            size="small"
                            icon={<EyeOutlined />}
                            loading={photoLoadingKey === `enc-${enc._id}-back`}
                            onClick={() => openEvidencePhoto(
                              `enc-${enc._id}-back`,
                              () => OnDemandPhotoService.getEncounterCardPhotoUrl(enc._id, 'back'),
                              {
                                title: `Blood card (back) — ${enc.firstName} ${enc.lastName}`,
                                subtitle: enc.capturedAt ? `Captured ${dayjs(enc.capturedAt).format('MMM D, YYYY h:mm A')}` : ''
                              }
                            )}
                          >
                            Back
                          </Button>
                          {enc.consentAcknowledged && (
                            <span style={{ fontSize: 11, color: '#52c41a' }}>
                              <CheckCircleOutlined /> consent
                            </span>
                          )}
                          {enc.voidedAt && (
                            <span style={{ fontSize: 11, color: '#ff4d4f' }}>
                              voided {dayjs(enc.voidedAt).format('MMM D')}
                            </span>
                          )}
                        </div>
                        {enc.voidReason && (
                          <div style={{ fontSize: 12, color: '#ff4d4f' }}>Void reason: {enc.voidReason}</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </FormSection>
            )}

            {/* Status History */}
            <FormSection>
              <div className="section-title">
                <HistoryOutlined /> Status History
              </div>
              <StatusHistoryList>
                {(detailEvent.statusHistory || []).slice().reverse().map((entry, idx) => (
                  <div className="history-item" key={idx}>
                    <div className="history-dot" />
                    <div className="history-content">
                      <div className="history-status">{entry.status}</div>
                      <div className="history-meta">
                        {dayjs(entry.changedAt).format('MMM D, YYYY h:mm A')}
                        {entry.changedBy && ` by ${entry.changedBy.firstName || ''} ${entry.changedBy.lastName || ''}`}
                      </div>
                      {entry.reason && <div className="history-reason">{entry.reason}</div>}
                    </div>
                  </div>
                ))}
              </StatusHistoryList>
            </FormSection>
          </div>
        ) : null}
      </StyledModal>

      {/* Inline evidence-photo viewer. Used for kit photos, drop-off proofs,
          and blood-card front/back. Click image to zoom via AntD previewer. */}
      <Modal
        title={photoViewer.title || 'Evidence photo'}
        open={photoViewer.open}
        onCancel={() => setPhotoViewer({ open: false, url: null, title: '', subtitle: '' })}
        footer={null}
        width={720}
        destroyOnClose
      >
        {photoViewer.subtitle && (
          <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 12 }}>{photoViewer.subtitle}</div>
        )}
        {photoViewer.url && (
          <div style={{ textAlign: 'center', background: '#000', borderRadius: 8, padding: 8 }}>
            <Image
              src={photoViewer.url}
              alt={photoViewer.title}
              style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
              preview={{ mask: 'Click to zoom' }}
            />
          </div>
        )}
      </Modal>

      {/* Filter Drawer */}
      <FilterDrawer
        visible={filterDrawerVisible}
        onClose={() => setFilterDrawerVisible(false)}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        staffingFilter={staffingFilter}
        setStaffingFilter={setStaffingFilter}
        onApply={() => setFilterDrawerVisible(false)}
        onClear={() => {
          setStatusFilter(undefined);
          setStaffingFilter(undefined);
          setFilterDrawerVisible(false);
        }}
      />

      <CreateBookingModal
        visible={bookingModalVisible}
        forceMobileEvent
        onCancel={() => setBookingModalVisible(false)}
        onSuccess={() => {
          setBookingModalVisible(false);
          fetchEvents();
        }}
      />
    </PageContainer>
  );
};

export default EventsPage;
